import { type ReactNode, useEffect, useRef, useState } from 'react';

import type { LibraryBookPreview } from './LibraryBookObject.types';

/**
 * The book on a Library card, as a solid on web: two boards, a spine, a block
 * of pages with its edges, and — once `open` — the real pages of the book
 * turning.
 *
 * Geometry (px, z towards the viewer, x = 0 at the spine): the closed front
 * board lies on z 0…BOARD, the back board on −depth−BOARD…−depth, the pages in
 * between. Opening swings the front board with the top half of the pages about
 * a hinge at the fold (x ≈ 0, mid-depth): both boards end up on the table and
 * both halves with their pages at mid-depth (S), meeting at a hairline seam —
 * the sewn fold — with only the pages' own shading for the crease. The spine
 * shows on the closed book and fades as it opens (it lies under the fold).
 *
 * Every turning page is a chain of hinged strips, so it bends as it turns. A
 * page's height in the stack is fixed in its own frame and it turns about an
 * axis just above the stack, which makes the order reverse by itself as it
 * lands: no two pages ever share a plane, so nothing flickers.
 *
 * Driven frame by frame (one rAF loop while open or closing), writing styles
 * directly: React renders the structure only.
 */

const INSET = 3; // the boards overhang the pages by this much
const BOARD = 2; // board thickness
const R_SPINE = 2; // corner radius on the spine side
const R_EDGE = 4; // corner radius on the fore-edge side
const SEGMENTS = 8; // strips per page that bends
const CURL = 9; // degrees between strips at the height of a turn
const GAP = 0.25; // px between stacked pages
const OPEN_MS = 1100;
const CLOSE_MS = 850;
const FIRST_TURN_AFTER_MS = 450;
/** How sharply each strip, from the fold out, rises out of the binding (× the curve's angle). */
const CURVE_SHAPE = [1, 0.55, 0.25, 0.08, 0, 0, 0, 0];
const HOLD_MS = 2000;
const STAGGER_MS = 220; // mean gap between pages leaving, in a riffle

const PAPER = '#f6f3ec';
// Page edges: one fine line per few leaves, darker towards the boards.
const LINES = '#faf7f0 0 0.7px, #d6cebd 0.7px 1.1px';
const EDGE_X = `repeating-linear-gradient(to right, ${LINES})`;
const EDGE_Y = `repeating-linear-gradient(to bottom, ${LINES})`;
// The crease: a page darkens softly as it curves into the fold.
const GUTTER_R =
  'linear-gradient(to right, rgba(0,0,0,0.2), rgba(0,0,0,0.06) 4%, transparent 11%)';
const GUTTER_L =
  'linear-gradient(to left, rgba(0,0,0,0.2), rgba(0,0,0,0.06) 4%, transparent 11%)';

const ease = (t: number) => (t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2);
/** Inverse of {@link ease}: when the eased curve reaches `y`. */
function easeInverse(y: number) {
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (ease(mid) < y) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}
const clamp01 = (t: number) => Math.min(1, Math.max(0, t));
const rad = (deg: number) => (deg * Math.PI) / 180;

/**
 * An open page's curve: it leaves the binding below the stack, rises steeply,
 * and lies flat a few strips out — the dip a sewn fold pulls into every open
 * book. `k` 0 = flat, 1 = fully relaxed. Returns each strip's angle (degrees,
 * ≥ 0, from the horizontal), how deep the fold sits, and where the page is flat.
 */
function curve(k: number, strip: number, half: number) {
  return curveTo(Math.min(half * 0.95, strip * 0.75) * k, strip);
}

/** The curve that leaves the fold `dip` below the stack and rises to meet it. */
function curveTo(dip: number, strip: number) {
  const rise = (a: number) =>
    CURVE_SHAPE.reduce((sum, s) => sum + strip * Math.sin(rad(a * s)), 0);
  let lo = 0;
  let hi = 85;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (rise(mid) < dip) lo = mid;
    else hi = mid;
  }
  const angles = CURVE_SHAPE.map((s) => lo * s);
  // The page's profile, strip by strip from the fold: x out, depth below the stack.
  const profile = [{ x: 0, depth: dip }];
  angles.forEach((a, i) => {
    if (CURVE_SHAPE[i] === 0) return;
    const last = profile[profile.length - 1];
    profile.push({
      x: last.x + strip * Math.cos(rad(a)),
      depth: last.depth - strip * Math.sin(rad(a)),
    });
  });
  return { angles, dip, profile };
}

/** Resolves once the strip is decoded, so a book never opens onto blank pages. */
export function preloadBookPages(src: string): Promise<void> {
  const img = new Image();
  img.src = src;
  return img.decode();
}

export interface LibraryBookObject3DProps {
  /** The cover as seen from the front (art and its overlays), filling its box. */
  face: ReactNode;
  /** The bare cover art, reused for the spine and the back cover. */
  art: ReactNode;
  /** Printed down the spine. */
  title?: string;
  depth: number;
  open: boolean;
  preview?: LibraryBookPreview;
}

interface Leaf {
  root: HTMLDivElement | null;
  segs: (HTMLDivElement | null)[];
  front: (HTMLDivElement | null)[];
  back: (HTMLDivElement | null)[];
  frontShade: (HTMLDivElement | null)[];
  backShade: (HTMLDivElement | null)[];
}

interface Turn {
  from: number;
  to: number;
  dir: 1 | -1;
  t0: number;
  start: number[];
  dur: number[];
  lift: number[]; // each page's height above the stack, in its own frame
  axis: number; // height of the turning axis above the pages
}

export function LibraryBookObject3D({
  face,
  art,
  title,
  depth,
  open,
  preview,
}: LibraryBookObject3DProps) {
  const half = depth / 2;
  const surface = -half; // where open pages lie

  const stops = preview?.stops ?? [];
  const pool = stops.length
    ? Math.max(
        stops[0],
        ...stops.map((s, i) => Math.abs(stops[(i + 1) % stops.length] - s)),
      )
    : 0;

  const [active, setActive] = useState(false);
  const group = useRef<HTMLDivElement>(null);
  const pagesArea = useRef<HTMLDivElement>(null);
  /** The two open pages, curved into the fold: right shows its front, left its back. */
  const spreadPages = useRef<{ right: Leaf | null; left: Leaf | null }>({
    right: null,
    left: null,
  });
  /** Head/tail faces of the page block, cut to the curve at the fold. */
  type Cut = { surfaceLast: HTMLDivElement[]; surfaceFirst: HTMLDivElement[] };
  const foldEdges = useRef<{ back: Cut; front: Cut }>({
    back: { surfaceLast: [], surfaceFirst: [] },
    front: { surfaceLast: [], surfaceFirst: [] },
  });
  const leftShadow = useRef<HTMLDivElement>(null);
  const spinePanel = useRef<HTMLDivElement>(null);
  const foldPaper = useRef<HTMLDivElement>(null);
  const leaves = useRef<Leaf[]>([]);
  const eng = useRef({
    phase: 'closed' as 'closed' | 'opening' | 'open' | 'closing',
    bend: 0, // the open pages' curve, 0…1 — follows the swing
    paged: false, // the open pages have their page images
    t0: 0,
    angle: 0,
    angleFrom: 0,
    spread: 0,
    stop: 0,
    next: 0,
    turn: null as Turn | null,
    raf: 0,
  });
  // Latest props for the loop, which outlives renders.
  const live = useRef({ preview, half, surface });
  live.current = { preview, half, surface };

  useEffect(() => {
    const e = eng.current;
    const now = performance.now();
    if (open) {
      if (e.phase === 'open' || e.phase === 'opening') return;
      setActive(true);
      e.phase = 'opening';
    } else {
      if (e.phase === 'closed' || e.phase === 'closing') return;
      settle();
      e.phase = 'closing';
    }
    e.t0 = now;
    e.angleFrom = e.angle;
    if (!e.raf) e.raf = requestAnimationFrame(frame);
  }, [open]); // the loop reads refs only

  useEffect(() => () => cancelAnimationFrame(eng.current.raf), []);

  // ── the loop ────────────────────────────────────────────────────────────
  /** Where page `page` sits in the sheet (`columns` to a row), as a background position. */
  function pagePosition(page: number) {
    const sheet = live.current.preview;
    const cols = sheet?.columns ?? 1;
    const rows = Math.ceil(((sheet?.spreads ?? 1) * 2) / cols);
    const x = cols > 1 ? ((page % cols) / (cols - 1)) * 100 : 0;
    const y = rows > 1 ? (Math.floor(page / cols) / (rows - 1)) * 100 : 0;
    return `${x}% ${y}%`;
  }

  function showSpread(left: number, right: number) {
    const { right: r, left: l } = spreadPages.current;
    for (const el of r?.front ?? [])
      if (el) el.style.backgroundPosition = pagePosition(right);
    for (const el of l?.back ?? [])
      if (el) el.style.backgroundPosition = pagePosition(left);
  }
  /**
   * Poses a page: strip i at `angles[i]` (degrees; 0 = flat on the right, −180 =
   * flat on the left), the chain hinged at the fold (`foldX`, `fold`), `lift`
   * above it in the page's own frame, turning about an axis `axis` above the fold.
   */
  function pose(
    l: Leaf,
    angles: number[],
    fold: number,
    lift: number,
    axis: number,
    foldX = 0,
  ) {
    if (!l.root) return;
    // Shade by how far a strip faces away, blended into the next strip's, so
    // the page reads as one smooth surface rather than facets.
    const dark = [...angles, angles[SEGMENTS - 1]].map((a) =>
      (0.3 * Math.abs(Math.sin(rad(a)))).toFixed(3),
    );
    let prev = 0;
    for (let i = 0; i < SEGMENTS; i++) {
      const abs = angles[i];
      const shade = `rgba(0,0,0,${dark[i]}), rgba(0,0,0,${dark[i + 1]})`;
      if (l.frontShade[i])
        l.frontShade[i]!.style.background = `linear-gradient(to right, ${shade})`;
      if (l.backShade[i])
        l.backShade[i]!.style.background = `linear-gradient(to left, ${shade})`;
      if (i === 0)
        l.root.style.transform = `translate3d(${foldX}px,0,${fold + axis}px) rotateY(${abs}deg) translateZ(${lift - axis}px)`;
      else if (l.segs[i]) l.segs[i]!.style.transform = `rotateY(${abs - prev}deg)`;
      prev = abs;
    }
  }
  /** The open pages' curve now, and the fold depth that goes with it. */
  function currentCurve() {
    const w = (pagesArea.current?.offsetWidth ?? 0) / SEGMENTS;
    return curve(eng.current.bend, w, live.current.half);
  }
  /**
   * Lays the two open pages, and cuts the block's edges, to the current curve.
   * Both pages hang from ONE fold — the sewn binding, `dip` below the stack —
   * so they never part. The right page rises from it onto the bottom half; the
   * left rides the hinge at `angle`, curving just enough to meet its own half
   * from that same fold: none while it stands upright, all of it once it lies open.
   */
  function placeSpread(angle: number) {
    const { surface: S, half: h } = live.current;
    const w = (pagesArea.current?.offsetWidth ?? 0) / SEGMENTS;
    const back = currentCurve();
    const fold = S - back.dip;
    const front = curveTo(Math.max(0, -back.dip * Math.cos(rad(angle))), w);
    const { right, left } = spreadPages.current;
    if (right)
      pose(
        right,
        back.angles.map((a) => -a),
        fold,
        0.1,
        0,
      );
    if (left)
      pose(
        left,
        front.angles.map((a) => angle + a),
        fold,
        0.1,
        0,
      );
    if (foldPaper.current)
      foldPaper.current.style.transform = `translateZ(${fold - 0.3}px)`;
    // Head and tail of each half follow its page's curve down into the fold.
    const cut = (edges: Cut, profile: { x: number; depth: number }[]) => {
      const pts = (fromSurface: (d: number) => number) =>
        profile.map((p) => `${p.x}px ${fromSurface(p.depth / h) * 100}%`);
      const last = pts((d) => 1 - d)
        .reverse()
        .join(', ');
      for (const el of edges.surfaceLast)
        el.style.clipPath = `polygon(0 0, 100% 0, 100% 100%, ${last})`;
      const first = pts((d) => d).join(', ');
      for (const el of edges.surfaceFirst)
        el.style.clipPath = `polygon(${first}, 100% 0, 100% 100%, 0 100%)`;
    };
    cut(foldEdges.current.back, back.profile);
    cut(foldEdges.current.front, front.profile);
  }
  function hideLeaves() {
    for (const l of leaves.current) if (l?.root) l.root.style.visibility = 'hidden';
  }
  /** Drops a turn in progress where it stands, pages landed or not. */
  function settle() {
    const e = eng.current;
    const t = e.turn;
    if (!t) return;
    const landed = t.start.filter(
      (_, j) => turnProgress(t, j, performance.now()) >= 0.5,
    ).length;
    e.spread = t.from + t.dir * landed;
    e.turn = null;
    hideLeaves();
    showSpread(2 * e.spread, 2 * e.spread + 1);
  }
  function turnProgress(t: Turn, j: number, now: number) {
    return ease(clamp01((now - t.t0 - t.start[j]) / t.dur[j]));
  }

  function startTurn(now: number) {
    const e = eng.current;
    const to = stops[e.stop % stops.length];
    const from = e.spread;
    if (to === from) {
      e.stop += 1;
      e.next = now + HOLD_MS;
      return;
    }
    const dir = to > from ? 1 : -1;
    const k = Math.abs(to - from);
    // Pages leave slowly, rush in the middle, and slow down again onto the figure.
    const span = STAGGER_MS * (k - 1);
    const start = Array.from({ length: k }, (_, j) =>
      k > 1 ? span * easeInverse(j / (k - 1)) : 0,
    );
    // Each page turns at the same pace (the first and the last a little slower),
    // so they land in the order they left and never bunch up.
    const dur = Array.from({ length: k }, (_, j) =>
      j === k - 1 ? 950 : j === 0 ? 760 : 650,
    );
    const lift = Array.from({ length: k }, (_, j) => (dir > 0 ? k - j : j + 1) * GAP);
    const turn: Turn = {
      from,
      to,
      dir,
      t0: now,
      start,
      dur,
      lift,
      axis: (k * GAP) / 2 + 0.3,
    };
    leaves.current.forEach((l, j) => {
      if (!l?.root) return;
      if (j >= k) {
        l.root.style.visibility = 'hidden';
        return;
      }
      // The leaf between spreads s and s + 1: page 2s + 1 on its front, 2s + 2 behind.
      const s = dir > 0 ? from + j : from - j - 1;
      for (const el of l.front)
        if (el) el.style.backgroundPosition = pagePosition(2 * s + 1);
      for (const el of l.back)
        if (el) el.style.backgroundPosition = pagePosition(2 * s + 2);
      l.root.style.visibility = 'visible';
    });
    showSpread(2 * (dir > 0 ? from : to), 2 * (dir > 0 ? to : from) + 1);
    e.turn = turn;
    stepTurn(now);
  }

  function stepTurn(now: number) {
    const e = eng.current;
    const t = e.turn!;
    const { surface: S } = live.current;
    const { angles: rest, dip } = currentCurve();
    let ahead = 1;
    let done = true;
    t.start.forEach((_, j) => {
      // A page never overtakes the one turned before it.
      const q = Math.min(turnProgress(t, j, now), ahead);
      ahead = q;
      if (q < 1) done = false;
      const l = leaves.current[j];
      if (!l?.root) return;
      // From its resting curve on one side to the mirrored curve on the other.
      const from = t.dir > 0 ? 0 : 1;
      const at = from + (1 - 2 * from) * q;
      // The free edge leads as the page lifts and trails as it lands.
      const bend = -t.dir * CURL * Math.sin(Math.PI * q) * (1.25 - q * 0.6);
      const angles = rest.map((a, i) =>
        Math.max(-180, Math.min(0, -a + (-180 + 2 * a) * at + i * bend)),
      );
      pose(l, angles, S - dip, t.lift[j], t.axis);
    });
    if (done) {
      e.spread = t.to;
      e.turn = null;
      hideLeaves();
      showSpread(2 * t.to, 2 * t.to + 1);
      e.stop += 1;
      e.next = now + HOLD_MS;
    }
  }

  function frame(now: number) {
    const e = eng.current;
    const { half: h } = live.current;
    if (e.phase === 'opening') {
      if (e.angleFrom === 0 && e.angle === 0) showSpread(2 * e.spread, 2 * e.spread + 1);
      const p = clamp01((now - e.t0) / OPEN_MS);
      e.angle = e.angleFrom + (-180 - e.angleFrom) * ease(p);
      if (p >= 1) {
        e.phase = 'open';
        e.next = now + FIRST_TURN_AFTER_MS;
      }
    } else if (e.phase === 'closing') {
      const p = clamp01((now - e.t0) / CLOSE_MS);
      e.angle = e.angleFrom * (1 - ease(p));
      if (p >= 1) {
        e.phase = 'closed';
        e.paged = false;
        e.angle = 0;
        e.spread = 0;
        e.stop = 0;
      }
    } else if (e.phase === 'open' && live.current.preview?.stops.length) {
      if (!e.turn && now >= e.next) startTurn(now);
      else if (e.turn) stepTurn(now);
    }
    placeHinge(e.angle, h);
    // The pages relax into their curve as the book swings open, and straighten
    // as it closes.
    e.bend = ease(clamp01((-e.angle - 50) / 130));
    // The open pages mount a frame after the book starts opening: give them
    // their pages as soon as they exist.
    if (!e.paged && spreadPages.current.right?.root) {
      showSpread(2 * e.spread, 2 * e.spread + 1);
      e.paged = true;
    }
    placeSpread(e.angle);
    if (e.phase === 'closed') {
      e.raf = 0;
      setActive(false);
      return;
    }
    e.raf = requestAnimationFrame(frame);
  }

  /** Swings the front half to `angle` about the fold; the spine fades as it opens. */
  function placeHinge(angle: number, h: number) {
    if (group.current)
      group.current.style.transform = `translateZ(${-h}px) rotateY(${angle}deg) translateZ(${h}px)`;
    if (leftShadow.current)
      leftShadow.current.style.opacity = String(clamp01((-angle - 150) / 30));
    if (spinePanel.current)
      spinePanel.current.style.opacity = String(clamp01(1 + angle / 70));
    // Paper under the fold, so the seam where the halves meet never shows through.
    if (foldPaper.current) foldPaper.current.style.opacity = String(clamp01(-angle / 90));
  }

  // ── structure ───────────────────────────────────────────────────────────
  const strip: React.CSSProperties | undefined =
    active && preview
      ? {
          backgroundImage: `url("${preview.src}")`,
          backgroundSize: `${preview.columns * 100}% ${Math.ceil((preview.spreads * 2) / preview.columns) * 100}%`,
        }
      : undefined;
  const px = (n: number) => `${n}px`;
  const pagesBox = { left: 0, right: px(INSET), top: px(INSET), bottom: px(INSET) };
  const keep = (list: HTMLDivElement[]) => (el: HTMLDivElement | null) => {
    if (el && !list.includes(el)) list.push(el);
  };
  /**
   * One half of the page block, from zTop down by half: its fore-edge, head and
   * tail. `front`: the half on the hinge, whose open surface is its deep side.
   */
  const block = (zTop: number, front: boolean) => (
    <>
      {/* Its floor against the board: paper, so nothing dark shows through a gap. */}
      <div
        className="absolute"
        style={{
          ...pagesBox,
          backgroundColor: PAPER,
          transform: `translateZ(${front ? zTop - 0.05 : zTop - half + 0.05}px)`,
        }}
      />
      <div
        className="absolute origin-right"
        style={{
          right: px(INSET),
          top: px(INSET),
          bottom: px(INSET),
          width: px(half),
          background: `linear-gradient(to right, rgba(0,0,0,0.12), transparent 30%, transparent 70%, rgba(0,0,0,0.12)), ${EDGE_X}`,
          transform: `translateZ(${zTop}px) rotateY(-90deg)`,
        }}
      />
      {[false, true].map((tail) => (
        <div
          key={String(tail)}
          // Which row of the face lies at the open surface: the tail's last on the
          // back half, the head's last on the front (it is flipped open).
          ref={keep(
            foldEdges.current[front ? 'front' : 'back'][
              tail !== front ? 'surfaceLast' : 'surfaceFirst'
            ],
          )}
          className={`absolute ${tail ? 'origin-bottom' : 'origin-top'}`}
          style={{
            left: 0,
            right: px(INSET),
            height: px(half),
            [tail ? 'bottom' : 'top']: px(INSET),
            background: `linear-gradient(rgba(0,0,0,${tail ? 0.16 : 0.04}), rgba(0,0,0,${tail ? 0.16 : 0.04})), ${EDGE_Y}`,
            transform: `translateZ(${zTop}px) rotateX(${tail ? 90 : -90}deg)`,
          }}
        />
      ))}
    </>
  );
  /** A board: its inside (the cover turned in) and its three edges. */
  const board = (zBack: number, inside: number) => (
    <>
      <div
        className="absolute inset-0 overflow-hidden"
        style={{
          borderRadius: `${R_SPINE}px ${R_EDGE}px ${R_EDGE}px ${R_SPINE}px`,
          transform: `translateZ(${inside}px)`,
        }}>
        {art}
        <div className="absolute inset-0 bg-black/45" />
      </div>
      {(['right', 'top', 'bottom'] as const).map((side) => (
        <div
          key={side}
          className={`absolute overflow-hidden origin-${side}`}
          style={{
            // Stop short of the rounded corners, so no square outline shows round them.
            ...(side === 'right'
              ? { right: 0, top: px(R_EDGE), bottom: px(R_EDGE), width: px(BOARD) }
              : { left: px(R_SPINE), right: px(R_EDGE), [side]: 0, height: px(BOARD) }),
            transform: `translateZ(${zBack + BOARD}px) ${side === 'right' ? 'rotateY(-90deg)' : side === 'top' ? 'rotateX(-90deg)' : 'rotateX(90deg)'}`,
          }}>
          {art}
          <div className="absolute inset-0 bg-black/40" />
        </div>
      ))}
    </>
  );
  const shadow = (z: number, mirrored: boolean, ref?: React.Ref<HTMLDivElement>) => (
    <div
      ref={ref}
      className={`pointer-events-none absolute -inset-[4%] ${mirrored ? 'opacity-0' : ''}`}
      style={{
        transform: `translateZ(${z}px) translate(${mirrored ? -4 : 4}%, 5%)`,
        background: 'radial-gradient(closest-side, rgba(0,0,0,0.32), transparent)',
      }}
    />
  );

  return (
    <div className="absolute inset-0 [transform-style:preserve-3d]">
      {shadow(-depth - BOARD - 1, false)}

      {/* The back board and the bottom half of the pages: they stay put. */}
      {board(-depth - BOARD, -depth - 0.05)}
      {block(-half, false)}

      {/* The spine: the cover's cloth round a rounded back, headbands at head and
          tail, the title running down it. It fades as the book opens. */}
      <div
        ref={spinePanel}
        className="absolute inset-y-0 left-0 origin-left overflow-hidden [backface-visibility:hidden]"
        style={{
          width: px(depth + 2 * BOARD),
          borderRadius: px(R_SPINE),
          transform: `translateZ(${-depth - BOARD}px) rotateY(-90deg)`,
        }}>
        {/* The cover's own cloth: only its outermost edge (plain ground and the
            stripe at the foot), stretched round the spine. */}
        <div className="absolute inset-y-0 left-0 w-[2500%] [&_img]:object-fill">
          {art}
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.55),rgba(0,0,0,0.12)_22%,rgba(255,255,255,0.14)_42%,rgba(0,0,0,0.05)_60%,rgba(0,0,0,0.5))]" />
        <div className="absolute inset-x-[12%] top-[6%] h-px bg-white/35" />
        <div className="absolute inset-x-[12%] bottom-[6%] h-px bg-white/35" />
        {title ? (
          <div
            className="absolute inset-x-0 bottom-[12%] top-[12%] flex items-center overflow-hidden whitespace-nowrap font-semibold text-white/85"
            style={{
              writingMode: 'vertical-rl',
              fontSize: px(Math.max(6, Math.min(11, (depth + 2 * BOARD) * 0.42))),
              letterSpacing: '0.04em',
            }}>
            {title}
          </div>
        ) : null}
      </div>
      {/* The fold: paper under the seam where the two open halves meet. */}
      <div
        ref={foldPaper}
        className="absolute opacity-0"
        style={{
          top: px(INSET),
          bottom: px(INSET),
          left: px(-1.5),
          width: px(3),
          background: `linear-gradient(to right, #d9d2c3, ${PAPER} 50%, #d9d2c3)`,
          transform: `translateZ(${surface}px)`,
        }}
      />

      {/* The front board and the top half of the pages, on the hinge. */}
      <div
        ref={group}
        className="absolute inset-0 origin-left [transform-style:preserve-3d]">
        {shadow(BOARD + 0.5, true, leftShadow)}
        {block(0, true)}
        {board(0, 0.05)}
        <div
          className="absolute inset-0 [backface-visibility:hidden]"
          style={{ transform: `translateZ(${BOARD + 0.05}px)` }}>
          {face}
        </div>
      </div>

      {/* Pages turning. */}
      {active && preview ? (
        <div
          ref={pagesArea}
          className="absolute [transform-style:preserve-3d]"
          style={pagesBox}>
          {(['right', 'left'] as const).map((side) => (
            <LeafView
              key={side}
              strip={strip}
              visible
              register={(l) => {
                spreadPages.current[side] = l;
              }}
            />
          ))}
          {Array.from({ length: pool }, (_, j) => (
            <LeafView
              key={j}
              strip={strip}
              register={(l) => {
                leaves.current[j] = l;
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** One turning page: SEGMENTS strips, each hinged on the one before. */
function LeafView({
  strip,
  register,
  visible = false,
}: {
  strip: React.CSSProperties | undefined;
  register: (leaf: Leaf) => void;
  /** Shown from the start (the open pages); turning pages show when they turn. */
  visible?: boolean;
}) {
  const leaf = useRef<Leaf>({
    root: null,
    segs: [],
    front: [],
    back: [],
    frontShade: [],
    backShade: [],
  });
  useEffect(() => register(leaf.current));
  const l = leaf.current;
  // Each strip reaches 1px into the next, so no hairline of background shows
  // where two strips meet; the page image stays aligned (100% − 1px = one strip).
  const face = (i: number, back: boolean) => (
    <div
      className="absolute inset-y-0 left-0 -right-px overflow-hidden [backface-visibility:hidden]"
      style={{ backgroundColor: PAPER, transform: back ? 'rotateY(180deg)' : undefined }}>
      <div
        ref={(el) => {
          (back ? l.back : l.front)[i] = el;
        }}
        className="absolute inset-y-0"
        style={{
          ...strip,
          width: `calc(${SEGMENTS} * (100% - 1px))`,
          left: back
            ? `calc(${-(SEGMENTS - 1 - i)} * (100% - 1px) + 1px)`
            : `calc(${-i} * (100% - 1px))`,
        }}
      />
      {i === 0 ? (
        <div
          className="absolute inset-0"
          style={{
            background: back ? GUTTER_L : GUTTER_R,
            width: `${SEGMENTS * 100}%`,
            right: back ? 0 : undefined,
            left: back ? 'auto' : 0,
          }}
        />
      ) : null}
      <div
        ref={(el) => {
          (back ? l.backShade : l.frontShade)[i] = el;
        }}
        className="absolute inset-0"
      />
    </div>
  );
  const seg = (i: number): ReactNode => (
    <div
      ref={(el) => {
        if (i === 0) l.root = el;
        l.segs[i] = el;
      }}
      className={`absolute inset-y-0 origin-left [transform-style:preserve-3d] ${i === 0 ? 'left-0' : 'left-full w-full'}`}
      style={{
        width: i === 0 ? `${100 / SEGMENTS}%` : undefined,
        visibility: i === 0 && !visible ? 'hidden' : undefined,
      }}>
      {face(i, false)}
      {face(i, true)}
      {i < SEGMENTS - 1 ? seg(i + 1) : null}
    </div>
  );
  return seg(0);
}
